import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ShieldCheck, 
  FileText, 
  RotateCcw, 
  Search, 
  ChevronRight, 
  Printer, 
  Copy, 
  Check, 
  Mail, 
  MapPin, 
  Phone, 
  ExternalLink, 
  ArrowLeft, 
  AlertTriangle, 
  CheckCircle2, 
  BookOpen, 
  Sparkles,
  Lock,
  Scale,
  Building2,
  Info
} from "lucide-react";
import { toast } from "sonner";
import PageSeo from "@/components/seo/PageSeo";
import Footer from "@/components/layout/Footer";

// ─── COMPLETE VERBATIM LEGAL DATA STORE ───────────────────────────────────────
const LEGAL_DATA = {
  privacy: {
    id: "privacy",
    path: "/privacy-policy/",
    title: "Privacy Policy",
    eyebrow: "Data Protection & Privacy Rights",
    lastUpdated: "July 12, 2024",
    icon: ShieldCheck,
    color: "#00FF88",
    badge: "ISO 21001:2018 Compliant",
    summary: "This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your information when You use the Service and tells You about Your privacy rights and how the law protects You. We use Your Personal data to provide and improve the Service. By using the Service, You agree to the collection and use of information in accordance with this Privacy Policy.",
    highlights: [
      { label: "100% Encrypted", desc: "Industry-standard data security and storage protocols." },
      { label: "Zero Spam", desc: "We never sell your personal information to unauthorized third parties." },
      { label: "User Control", desc: "Right to access, update, or request deletion of your personal data anytime." }
    ],
    sections: [
      {
        id: "interpretation-and-definitions",
        title: "Interpretation and Definitions",
        subsections: [
          {
            subtitle: "Interpretation",
            content: [
              "The words of which the initial letter is capitalized have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural."
            ]
          },
          {
            subtitle: "Definitions",
            content: [
              "For the purposes of this Privacy Policy:",
              "• Account: Means a unique account created for You to access our Service or parts of our Service.",
              "• Affiliate: Means an entity that controls, is controlled by or is under common control with a party, where 'control' means ownership of 50% or more of the shares, equity interest or other securities entitled to vote for election of directors or other managing authority.",
              "• Company: (referred to as either 'the Company', 'We', 'Us' or 'Our' in this Agreement) refers to Jama Dhan Stock Market Institute, Jaipur, Rajasthan.",
              "• Cookies: Small files placed on Your computer, mobile device or any other device by a website, containing details of Your browsing history on that website among its many uses.",
              "• Country: Refers to Rajasthan, India.",
              "• Device: Means any device that can access the Service such as a computer, a cellphone or a digital tablet.",
              "• Personal Data: Any information that relates to an identified or identifiable individual.",
              "• Service: Refers to the Website and related educational platforms.",
              "• Service Provider: Means any natural or legal person who processes the data on behalf of the Company. It refers to third-party companies or individuals employed by the Company to facilitate the Service, to provide the Service on behalf of the Company, to perform services related to the Service or to assist the Company in analyzing how the Service is used.",
              "• Usage Data: Refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself (for example, the duration of a page visit).",
              "• Website: Refers to Jama Dhan, accessible from https://www.Jamadhan.com",
              "• You: Means the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable."
            ]
          }
        ]
      },
      {
        id: "collecting-and-using-data",
        title: "Collecting and Using Your Personal Data",
        subsections: [
          {
            subtitle: "Types of Data Collected",
            content: [
              "Personal Data: While using Our Service, We may ask You to provide Us with certain personally identifiable information that can be used to contact or identify You. Personally identifiable information may include, but is not limited to:",
              "1. Email address",
              "2. First name and last name",
              "3. Phone number",
              "4. Address, State, Province, ZIP/Postal code, City",
              "5. Usage Data"
            ]
          },
          {
            subtitle: "Usage Data",
            content: [
              "Usage Data is collected automatically when using the Service.",
              "Usage Data may include information such as Your Device's Internet Protocol address (e.g. IP address), browser type, browser version, the pages of our Service that You visit, the time and date of Your visit, the time spent on those pages, unique device identifiers and other diagnostic data.",
              "When You access the Service by or through a mobile device, We may collect certain information automatically, including, but not limited to, the type of mobile device You use, Your mobile device unique ID, the IP address of Your mobile device, Your mobile operating system, the type of mobile Internet browser You use, unique device identifiers and other diagnostic data.",
              "We may also collect information that Your browser sends whenever You visit our Service or when You access the Service by or through a mobile device."
            ]
          },
          {
            subtitle: "Tracking Technologies and Cookies",
            content: [
              "We use Cookies and similar tracking technologies to track the activity on Our Service and store certain information. Tracking technologies used are beacons, tags, and scripts to collect and track information and to improve and analyze Our Service. The technologies We use may include:",
              "• Cookies or Browser Cookies: A cookie is a small file placed on Your Device. You can instruct Your browser to refuse all Cookies or to indicate when a Cookie is being sent. However, if You do not accept Cookies, You may not be able to use some parts of our Service. Unless you have adjusted Your browser setting so that it will refuse Cookies, our Service may use Cookies.",
              "• Web Beacons: Certain sections of our Service and our emails may contain small electronic files known as web beacons (also referred to as clear gifs, pixel tags, and single-pixel gifs) that permit the Company, for example, to count users who have visited those pages or opened an email and for other related website statistics (for example, recording the popularity of a certain section and verifying system and server integrity).",
              "Cookies can be 'Persistent' or 'Session' Cookies. Persistent Cookies remain on Your personal computer or mobile device when You go offline, while Session Cookies are deleted as soon as You close Your web browser.",
              "We use both Session and Persistent Cookies for the purposes set out below:",
              "1. Necessary / Essential Cookies (Session Cookies): Administered by Us. Purpose: Essential to provide You with services available through the Website and to enable You to use some of its features. They help to authenticate users and prevent fraudulent use of user accounts. Without these Cookies, the services that You have asked for cannot be provided.",
              "2. Cookies Policy / Notice Acceptance Cookies (Persistent Cookies): Administered by Us. Purpose: These Cookies identify if users have accepted the use of cookies on the Website.",
              "3. Functionality Cookies (Persistent Cookies): Administered by Us. Purpose: Allow us to remember choices You make when You use the Website, such as remembering your login details or language preference. The purpose of these Cookies is to provide You with a more personal experience and to avoid You having to re-enter your preferences every time You use the Website."
            ]
          }
        ]
      },
      {
        id: "use-of-personal-data",
        title: "Use of Your Personal Data",
        content: [
          "The Company may use Personal Data for the following purposes:",
          "• To provide and maintain our Service, including to monitor the usage of our Service.",
          "• To manage Your Account: To manage Your registration as a user of the Service. The Personal Data You provide can give You access to different functionalities of the Service that are available to You as a registered user.",
          "• For the performance of a contract: The development, compliance and undertaking of the purchase contract for the products, items or services You have purchased or of any other contract with Us through the Service.",
          "• To contact You: To contact You by email, telephone calls, SMS, or other equivalent forms of electronic communication, such as a mobile application's push notifications regarding updates or informative communications related to the functionalities, products or contracted services, including security updates, when necessary or reasonable for their implementation.",
          "• To provide You with news, special offers and general information about other goods, services and events which we offer that are similar to those that you have already purchased or enquired about unless You have opted not to receive such information.",
          "• To manage Your requests: To attend and manage Your requests to Us.",
          "• For business transfers: We may use Your information to evaluate or conduct a merger, divestiture, restructuring, reorganization, dissolution, or other sale or transfer of some or all of Our assets, whether as a going concern or as part of bankruptcy, liquidation, or similar proceeding, in which Personal Data held by Us about our Service users is among the assets transferred.",
          "• For other purposes: We may use Your information for other purposes, such as data analysis, identifying usage trends, determining the effectiveness of our promotional campaigns and to evaluate and improve our Service, products, services, marketing and your experience."
        ]
      },
      {
        id: "sharing-personal-information",
        title: "Sharing Your Personal Information",
        content: [
          "We may share Your personal information in the following situations:",
          "• With Service Providers: We may share Your personal information with Service Providers to monitor and analyze the use of our Service, to contact You.",
          "• For business transfers: We may share or transfer Your personal information in connection with, or during negotiations of, any merger, sale of Company assets, financing, or acquisition of all or a portion of Our business to another company.",
          "• With Affiliates: We may share Your information with Our affiliates, in which case we will require those affiliates to honor this Privacy Policy. Affiliates include Our parent company and any other subsidiaries, joint venture partners or other companies that We control or that are under common control with Us.",
          "• With business partners: We may share Your information with Our business partners to offer You certain products, services or promotions.",
          "• With other users: When You share personal information or otherwise interact in the public areas with other users, such information may be viewed by all users and may be publicly distributed outside.",
          "• With Your consent: We may disclose Your personal information for any other purpose with Your consent."
        ]
      },
      {
        id: "retention-and-transfer",
        title: "Retention & Transfer of Your Personal Data",
        content: [
          "Retention of Your Personal Data: The Company will retain Your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use Your Personal Data to the extent necessary to comply with our legal obligations (for example, if we are required to retain your data to comply with applicable laws), resolve disputes, and enforce our legal agreements and policies.",
          "The Company will also retain Usage Data for internal analysis purposes. Usage Data is generally retained for a shorter period of time, except when this data is used to strengthen the security or to improve the functionality of Our Service, or We are legally obligated to retain this data for longer time periods.",
          "Transfer of Your Personal Data: Your information, including Personal Data, is processed at the Company's operating offices and in any other places where the parties involved in the processing are located. It means that this information may be transferred to — and maintained on — computers located outside of Your state, province, country or other governmental jurisdiction where the data protection laws may differ than those from Your jurisdiction.",
          "Your consent to this Privacy Policy followed by Your submission of such information represents Your agreement to that transfer. The Company will take all steps reasonably necessary to ensure that Your data is treated securely and in accordance with this Privacy Policy."
        ]
      },
      {
        id: "delete-personal-data",
        title: "Delete Your Personal Data",
        content: [
          "You have the right to delete or request that We assist in deleting the Personal Data that We have collected about You.",
          "Our Service may give You the ability to delete certain information about You from within the Service.",
          "You may update, amend, or delete Your information at any time by signing in to Your Account, if you have one, and visiting the account settings section that allows you to manage Your personal information. You may also contact Us to request access to, correct, or delete any personal information that You have provided to Us.",
          "Please note, however, that We may need to retain certain information when we have a legal obligation or lawful basis to do so."
        ]
      },
      {
        id: "disclosure-and-security",
        title: "Disclosure & Security of Your Personal Data",
        content: [
          "Business Transactions: If the Company is involved in a merger, acquisition or asset sale, Your Personal Data may be transferred. We will provide notice before Your Personal Data is transferred and becomes subject to a different Privacy Policy.",
          "Law enforcement: Under certain circumstances, the Company may be required to disclose Your Personal Data if required to do so by law or in response to valid requests by public authorities (e.g. a court or a government agency).",
          "Other legal requirements: The Company may disclose Your Personal Data in the good faith belief that such action is necessary to: comply with a legal obligation, protect and defend the rights or property of the Company, prevent or investigate possible wrongdoing in connection with the Service, protect the personal safety of Users of the Service or the public, protect against legal liability.",
          "Security of Your Personal Data: The security of Your Personal Data is important to Us, but remember that no method of transmission over the Internet, or method of electronic storage is 100% secure. While We strive to use commercially acceptable means to protect Your Personal Data, We cannot guarantee its absolute security."
        ]
      },
      {
        id: "children-privacy-and-links",
        title: "Children's Privacy & Links to Other Websites",
        content: [
          "Children's Privacy: Our Service does not address anyone under the age of 13. We do not knowingly collect personally identifiable information from anyone under the age of 13. If You are a parent or guardian and You are aware that Your child has provided Us with Personal Data, please contact Us. If We become aware that We have collected Personal Data from anyone under the age of 13 without verification of parental consent, We take steps to remove that information from Our servers.",
          "Links to Other Websites: Our Service may contain links to other websites that are not operated by Us. If You click on a third party link, You will be directed to that third party's site. We strongly advise You to review the Privacy Policy of every site You visit. We have no control over and assume no responsibility for the content, privacy policies or practices of any third party sites or services."
        ]
      },
      {
        id: "changes-and-contact",
        title: "Changes to this Privacy Policy & Contact Us",
        content: [
          "Changes to this Privacy Policy: We may update Our Privacy Policy from time to time. We will notify You of any changes by posting the new Privacy Policy on this page. We will let You know via email and/or a prominent notice on Our Service, prior to the change becoming effective and update the 'Last updated' date at the top of this Privacy Policy. You are advised to review this Privacy Policy periodically for any changes.",
          "Contact Us: If you have any questions about this Privacy Policy, You can contact us:",
          "• By email: contactus@jamadhan.com",
          "• By visiting this page on our website: https://jamadhan.com/contact",
          "• Office Address: 3rd Floor, JamaDhan Stock Market Institute, Ajmer Road, Opposite MG Motors, Heera Nagar, Tagore Nagar, Jaipur, Rajasthan, 302021"
        ]
      }
    ]
  },

  terms: {
    id: "terms",
    path: "/terms-and-conditions/",
    title: "Terms & Conditions",
    eyebrow: "Website & Service Agreement",
    lastUpdated: "Effective for all Jamadhan Users",
    icon: FileText,
    color: "#00F0FF",
    badge: "User Terms of Service",
    summary: "This site is managed by JAMADHAN. When the terms 'we,' 'us,' and 'our' are used on this site, JAMADHAN is intended. JAMADHAN grants you, the user, access to this website and all of its information, tools, and services, provided that you comply with all of the terms, conditions, policies, and notices indicated below. By using our site and/or purchasing something from us, you agree to be bound by these Terms & Conditions.",
    highlights: [
      { label: "Educational Platform", desc: "All strategies, zones, and analysis are strictly for learning and paper practice." },
      { label: "Strict Code of Conduct", desc: "Zero tolerance for spam, harassment, unauthorized promotion, or abuse." },
      { label: "Risk Awareness", desc: "Trading involves genuine risk of capital loss; individual due diligence required." }
    ],
    sections: [
      {
        id: "acceptance-of-terms",
        title: "Acceptance of Terms & General Conditions",
        content: [
          "This site is managed by JAMADHAN. When the terms 'we,' 'us,' and 'our' are used on this site, JAMADHAN is intended. JAMADHAN grants you, the user, access to this website and all of its information, tools, and services, provided that you comply with all of the terms, conditions, policies, and notices indicated below.",
          "By using our site and/or purchasing something from us, you agree to be bound by the following terms and conditions ('Terms of Service', 'Terms'), including such other terms and conditions and policies that are linked herein and/or available by hyperlink. These Terms of Service are applicable to all site users, regardless of whether they are merely browsing, selling, purchasing, selling, or adding material.",
          "Please read these Terms & Conditions thoroughly before visiting or using our website. While using any portion of the site, you agree to be governed by these Terms and Conditions. If you do not accept ALL or SOME of the terms and conditions of this agreement, you cannot use the website or any of the services.",
          "Any new features introduced to the website are likewise subject to these terms and conditions. You can always view the most recent version of the Terms & Conditions on this page. We retain the right to update, modify, or replace any portion of these Terms of Service by posting updates and/or modifications to our website. It is your responsibility to periodically check this page to see if anything has changed. If you continue to use or visit the website after changes have been made and posted, you consent to the modifications."
        ]
      },
      {
        id: "services-and-promotion",
        title: "Our Services, Products & Promotion",
        content: [
          "Our Services / Products: Your level of access to https://jamadhan.com/ products and services will depend on whatever products or services you have purchased. Please be aware that we have the right to expel you from our Telegram Group or Community if you breach our rules or act inappropriately towards others. In addition, we have the right to make modifications to this agreement at any time, including the discontinuance of any product or service.",
          "Promotion: Please be advised that, for educational and promotional purposes, we may disclose profit/loss chat screenshots of our members or registered clients on our Telegram channel. These images may depict profit/loss claims submitted by our members or registered clients. Please note that we are not liable for their profit or loss, nor do we verify or confirm this information."
        ]
      },
      {
        id: "risk-disclosure",
        title: "Important Risk Disclosure & Risk Statement",
        content: [
          "Important Risk Disclosure: Each investor or trader is responsible for determining if the stock market is an appropriate investment for them. There is no assurance that the methods or approaches given will be lucrative, and losses are possible. Any samples provided are for illustrative purposes only and do not constitute an offer to buy or sell. The platform's writers, owners, publishers, and affiliates accept no responsibility for your trading performance. Trading entails significant risk. We would also want to underline that the value of stocks and investments might change, and investors may not recoup their initial investment amount. In trading, losing money is a genuine possibility, and traders must be mindful of this risk.",
          "Risk Disclosure Statement: Trading assets such as stocks, futures, and options may result in significant losses and may not be appropriate for all individuals. Before engaging in such trading, you should evaluate your financial circumstances and determine if it is suitable for you. Previous findings are not always indicative of future outcomes. On this website, we do not recommend any specific securities, including stocks, options, or commodities. These tools and services are intended to aid in the creation of a rule-based trading system and do not constitute investing advice."
        ]
      },
      {
        id: "due-diligence-and-declaration",
        title: "Do Your Own Research & User Declaration",
        content: [
          "Do Your Own Research: Before making any financial decisions based on our analysis, it is crucial to conduct your own research and consult with a specialist. We are not compensated by any company whose stocks appear on our website or in our services, and we have no financial stake in the outcome of any trades discussed. You are responsible for determining if trading stocks, futures, options, or other securities is appropriate for you depending on your financial situation. Also, there may be tax implications linked with gains or losses on trades; therefore, it is advisable to consult a tax professional for further information.",
          "Declaration by Users: I Fully understand. Jamadhan looks to offer a program that educates individuals to actively trade shares and/or other financial instruments for their own account at a financial institution. The course should not be considered as advice or solicitation to purchase or sell any security or investing plan, nor as preparation for becoming a licensed broker. The acquisition, sale, or provision of advice regarding any security or financial instrument is restricted to licensed industry representatives only. It is also recognised that Jamadhan is not liable for any gains or losses incurred as a result of the analysis presented on their channel, and that the buy/sell analysis supplied is for paper/dummy practice and instructional reasons only."
        ]
      },
      {
        id: "service-use-and-disclaimer",
        title: "Use of Jamadhan Services & Warranty Disclaimer",
        content: [
          "Notably, JAMADHAN retains the right to alter or amend these terms and conditions at any time and without previous notice. Your continued use of the JAMADHAN Services following such adjustments will constitute your acceptance of the terms and conditions as modified. You agree that JAMADHAN may, in its sole discretion and without prior notice, modify or discontinue the JAMADHAN Services, or any portion thereof, at any time and that JAMADHAN will not be liable to you or any third party for any such modification or discontinuance.",
          "Moreover, JAMADHAN Services are supplied 'as is' and 'as available' without any express, implied, or statutory representations or guarantees of any kind. JAMADHAN expressly disclaims all implied warranties of title, merchantability, fitness for a particular purpose, and non-infringement.",
          "Indemnification: By using the JAMADHAN Services, you agree to indemnify, defend, and hold harmless JAMADHAN, its affiliates, officers, directors, employees, agents, and third-party service providers from and against any and all claims, damages, obligations, losses, liabilities, costs, and expenses (including but not limited to attorney's fees) arising from or relating to your participation in or use of the Jamadhan services."
        ]
      },
      {
        id: "rules-of-use",
        title: "Rules of Use & Prohibited Conduct",
        content: [
          "To maintain the highest degree of efficiency and mutual respect among all users utilizing Jamadhan Services, you are banned from posting or making public the following materials:",
          "• You agree not to post or share any content that a reasonable person would recognize as being harmful, threatening, unlawful, defamatory, infringing, abusive, inflammatory, harassing, vulgar, obscene, fraudulent, invasive of privacy or publicity rights, hateful, or racially, ethnically, or otherwise objectionable to us or to any other member of JAMADHAN Services.",
          "• You may not post or distribute any form of unsolicited or unauthorized advertising, promotional materials, junk mail, spam, chain letters, pyramid schemes, or other solicitation that promotes a service or product or constitutes an invitation or inducement to participate in a specific investment or investment activity without our prior approval.",
          "• It is prohibited to post or distribute any private information of a third party, including addresses, phone numbers, email addresses, and credit card numbers.",
          "• You should not publish or distribute any content that contains software viruses, harmful computer code, files, or programs designed to interrupt, destroy, or limit the functionality of any computer software or hardware or telecommunications equipment.",
          "• You agree not to post or make public any content within the JAMADHAN Services that would constitute, encourage, or offer instructions for a criminal offense, violate the rights of any party, or otherwise create liability or violate any local, state, national, or international law.",
          "• Do not disclose anything that you are prohibited from disclosing by law, regulation, or contractual or fiduciary obligations. This includes insider information about stocks or financial products, as well as proprietary and secret information acquired or divulged through employment arrangements, nondisclosure agreements, or other similar undertakings.",
          "• Do not impersonate another person or misrepresent your identity, age, or affiliation with any person or organization.",
          "• Utilizing or attempting to use another user's account, service, or system without our express permission is strictly banned, as is creating a fictitious identity.",
          "• This implies that you may not intentionally alter or modify any information that conceals or misrepresents the origin of any content you or anyone associated with you transmits over JAMADHAN Services. This includes changing headers or other identifiers to conceal the true origin of the data.",
          "• You undertake not to use the JAMADHAN Services in any unlawful manner or in any manner that could cause damage, disable, overburden, or impair the functionality of the Services, or adversely impact the use of the Services by other members or by us."
        ]
      },
      {
        id: "external-links-and-content",
        title: "External Links & Third-Party Websites",
        content: [
          "The Jamadhan Services and their Content may contain connections to external, completely independent websites. We have no influence over the content of these external websites. If you decide to access any such links, you do so at your own risk. We do not guarantee the legitimacy, correctness, completeness, or authenticity of any linked site or the information contained within.",
          "You acknowledge and agree that you must evaluate and assume all risks associated with using any Content, that you should not rely on such Content, and that we and our Related Parties are in no way responsible for any Content or any loss or damage incurred as a result of using any Content posted or made available through Jamadhan Services."
        ]
      },
      {
        id: "user-content-and-licenses",
        title: "User Content & Commercial License Grant",
        content: [
          "Your Content: As a user of Jamadhan Services, you are responsible for any content you post or share with other users. You must make sure that the content you post or share is your own or that you have permission to do so. We don't pre-screen or approve Your Content, but we do have the right to look at it and take down anything that breaks our rules. It is up to you to make copies of Your Content that you post or store in Jamadhan Services. You must protect us from any claims of infringement or unauthorised use that a third party might make against us because of what you did.",
          "License Grant to Jamadhan: When you post something to Jamadhan Services, you give us permission to copy it and keep it on our platform. You represent and warrant that you have the right to post this content and that you are giving us a non-exclusive, transferable, worldwide licence to use, distribute, sublicense, publicly perform, publicly display, excerpt, translate, and reformat your content for any purpose, including commercial, advertising, or promotional purposes, in connection with Jamadhan Services. This licence also gives you the right to make derivative works or use your content in other works, as well as the right to give sublicenses for derivative works.",
          "Communication Consent: You agree that we can use your contact information to give you Jamadhan Services, make your profile more personal, and get in touch with you. You also agree to get mail, email, or phone calls from Jamadhan (business name) with messages, offers, information about events, and ads. Our related staff may look at your profile and track how you use Jamadhan Services and our websites so that we can make them better.",
          "Marketing & Automated Technology Consent: By agreeing to these Terms, you give Jamadhan (Business Name) and its Related Persons and other carefully chosen third parties permission to use your email address for both transactional and marketing purposes and to call and text you on your cell phone. You understand that you can tell info@Jamadhan.com at any time that you don't want to receive direct marketing by email or phone. You also give Jamadhan and its Related Parties permission to use automated technology to call you or send you recorded messages or texts about their products to the phone/cell number you gave them when you signed up and subscribed. You agree that giving this consent is not necessary to buy goods or services from Jamadhan.",
          "Warranty of Content Legality: You promise, warrant, and agree that any content you post on Jamadhan Services will not violate or infringe upon the rights of any third party, including but not limited to copyright, trademark, privacy, publicity, or other personal or proprietary rights. You also agree that your content will not break any laws, be used to break laws or contain anything that is libelous, defamatory, or otherwise illegal. You agree that if a third party accuses us of infringement, unauthorized use, or any other kind of claim because of what you did, you will pay for it and keep us safe."
        ]
      }
    ]
  },

  refunds: {
    id: "refunds",
    path: "/refund-and-cancellation-policy/",
    title: "Refund & Cancellation Policy",
    eyebrow: "Institutional Payment & Enrollment Policy",
    lastUpdated: "Applicable to all Jamadhan Courses & Programs",
    icon: RotateCcw,
    color: "#FFCC00",
    badge: "Strict Enrollment Policy",
    summary: "This Refund and Cancellation Policy outlines the enrollment, payment, course support, and retake terms for all educational courses, mentorship programs, and subscriptions provided by Jama Dhan Stock Market Institute.",
    highlights: [
      { label: "Non-Refundable", desc: "All course fees and program enrollments are non-refundable once processed." },
      { label: "Curriculum Support", desc: "Dedicated student learning assistance provided for all course topics." },
      { label: "Retake Subject to Availability", desc: "Course retakes and review sessions are subject to seat availability." }
    ],
    sections: [
      {
        id: "no-refunds-or-cancellations",
        title: "1. No Refunds or Cancellations",
        content: [
          "We do not offer partial or full refunds or cancellations for the amount paid for our courses, mentorship batches, elementary classes, or digital programs.",
          "Once an enrollment is completed and payment is authorized, access to the respective curriculum, materials, live classes, or community channels is initiated immediately, and the transaction is deemed non-refundable."
        ]
      },
      {
        id: "commitment-to-learning",
        title: "2. Commitment to Successful Learning",
        content: [
          "We are committed to providing our best efforts to ensure the successful completion of online and classroom learning.",
          "Our faculty and mentors strive to deliver structured institutional strategies, interactive Q&A doubt resolution, and practical case studies to maximize educational value for every student."
        ]
      },
      {
        id: "individual-outcomes",
        title: "3. Individual Learning Outcomes",
        content: [
          "Learning outcomes may vary based on individual time investment, practice, discipline, market experience, and ability to grasp and execute the material.",
          "Stock market trading and technical analysis require sustained effort, emotional control, and rule adherence. Jama Dhan does not guarantee specific monetary results or trading profits."
        ]
      },
      {
        id: "support-for-issues",
        title: "4. Support for Online Learning Issues",
        content: [
          "For any issues related to online learning access, recorded lecture playback, or platform connectivity, please contact our support team at contact@jamadhan.com or contactus@jamadhan.com.",
          "Clarifications, curriculum support, and mentor queries will be provided strictly for the course curriculum as outlined on our official website."
        ]
      },
      {
        id: "course-retake-policy",
        title: "5. Course Retake Policy",
        content: [
          "Retaking any course, batch, or live mentorship cycle is subject to seat availability and prior approval from the institute management.",
          "Students seeking revision sessions or batch transfers must contact academic administration in writing."
        ]
      },
      {
        id: "final-decision",
        title: "6. Final Decision",
        content: [
          "All decisions made by Jama Dhan Stock Market Institute regarding admissions, batch allocations, community access, and policy interpretations are final and binding."
        ]
      }
    ]
  }
};

export default function LegalPage({ type = "privacy" }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Determine active policy key based on prop or pathname
  const activeKey = useMemo(() => {
    if (type && LEGAL_DATA[type]) return type;
    const path = location.pathname.toLowerCase();
    if (path.includes("terms")) return "terms";
    if (path.includes("refund")) return "refunds";
    return "privacy";
  }, [type, location.pathname]);

  const activePolicy = LEGAL_DATA[activeKey] || LEGAL_DATA.privacy;
  const Icon = activePolicy.icon;

  const [searchQuery, setSearchQuery] = useState("");
  const [copied, setCopied] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState("");

  // Track scroll position for navbar & active TOC item
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);

      // TOC active highlight
      const sections = document.querySelectorAll("[data-legal-section]");
      let current = "";
      sections.forEach((sec) => {
        const top = sec.getBoundingClientRect().top;
        if (top <= 180) {
          current = sec.getAttribute("id");
        }
      });
      if (current) setActiveSectionId(current);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeKey]);

  // Scroll to top on tab switch
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setSearchQuery("");
  }, [activeKey]);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText("contactus@jamadhan.com");
    setCopied(true);
    toast.success("Contact email copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePrint = () => {
    window.print();
  };

  const scrollToSection = (secId) => {
    const el = document.getElementById(secId);
    if (el) {
      const yOffset = -90;
      const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  // Filter sections by search query
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return activePolicy.sections;
    const q = searchQuery.toLowerCase();
    return activePolicy.sections.filter((sec) => {
      const inTitle = sec.title.toLowerCase().includes(q);
      const inContent = sec.content?.some((c) => c.toLowerCase().includes(q));
      const inSubsections = sec.subsections?.some(
        (sub) =>
          sub.subtitle.toLowerCase().includes(q) ||
          sub.content.some((c) => c.toLowerCase().includes(q))
      );
      return inTitle || inContent || inSubsections;
    });
  }, [activePolicy, searchQuery]);

  return (
    <div className="min-h-screen bg-[#040508] text-[#CBD5E1] font-plex selection:bg-[#00FF88]/20 selection:text-white relative overflow-x-hidden">
      <PageSeo
        path={activePolicy.path}
        fallbackTitle={`${activePolicy.title} — Jama Dhan Stock Market Institute`}
        fallbackDescription={activePolicy.summary}
      />

      {/* ── AMBIENT BACKGROUND GLOWS ────────────────────────────────────────── */}
      <div className="pointer-events-none fixed -top-40 -left-40 w-[500px] h-[500px] bg-[radial-gradient(circle,rgba(0,255,136,0.06),transparent_70%)] blur-3xl z-0" />
      <div className="pointer-events-none fixed top-1/3 -right-40 w-[600px] h-[600px] bg-[radial-gradient(circle,rgba(0,240,255,0.05),transparent_70%)] blur-3xl z-0" />
      <div className="pointer-events-none fixed bottom-10 left-1/4 w-[500px] h-[500px] bg-[radial-gradient(circle,rgba(255,204,0,0.03),transparent_70%)] blur-3xl z-0" />
      <div className="pointer-events-none fixed inset-0 opacity-[0.02] [background-image:linear-gradient(rgba(255,255,255,.5)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.5)_1px,transparent_1px)] [background-size:64px_64px] z-0" />

      {/* ── TOP INSTITUTIONAL NAVIGATION BAR ─────────────────────────────────── */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#040508]/90 backdrop-blur-xl border-b border-white/[0.08] shadow-2xl py-3" : "bg-transparent py-5"
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-9 h-9 rounded-xl overflow-hidden shadow-[0_0_20px_rgba(0,255,136,0.25)] border border-[#00FF88]/30 group-hover:border-[#00FF88] transition-colors p-0.5 bg-black">
              <img src="/jamadhan-logo.png" alt="Jamadhan Logo" className="w-full h-full object-contain" />
            </div>
            <div className="flex flex-col">
              <span className="text-base sm:text-lg font-black text-white font-outfit tracking-tight group-hover:text-[#00FF88] transition-colors">
                JAMADHAN
              </span>
              <span className="text-[9px] font-mono tracking-widest text-[#00F0FF] uppercase">Stock Market Institute</span>
            </div>
          </Link>

          {/* Nav Links */}
          <nav className="hidden md:flex items-center gap-6 text-xs sm:text-sm font-medium font-outfit text-zinc-300">
            <Link to="/" className="hover:text-[#00FF88] transition-colors">Home</Link>
            <Link to="/about/" className="hover:text-[#00FF88] transition-colors">About</Link>
            <Link to="/#courses" className="hover:text-[#00FF88] transition-colors">Courses</Link>
            <Link to="/gallery/" className="hover:text-[#00FF88] transition-colors">Gallery</Link>
            <Link to="/drishti-ai/" className="hover:text-[#00FF88] transition-colors text-[#00F0FF] font-bold flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5" /> Drishti AI
            </Link>
            <Link to="/contact/" className="hover:text-[#00FF88] transition-colors">Contact</Link>
          </nav>

          {/* Action CTAs */}
          <div className="flex items-center gap-2.5">
            <button
              onClick={handleCopyEmail}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-xs text-zinc-300 hover:text-white hover:bg-white/[0.08] transition-all font-jetbrains"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-[#00FF88]" /> : <Copy className="w-3.5 h-3.5 text-[#00F0FF]" />}
              <span>{copied ? "Copied" : "Support Email"}</span>
            </button>
            <Link
              to="/admin-login"
              className="px-3.5 py-1.5 rounded-lg bg-[#00FF88] hover:bg-[#00FF88]/90 text-black font-bold text-xs font-outfit transition-all shadow-[0_0_15px_rgba(0,255,136,0.3)]"
            >
              Login
            </Link>
          </div>
        </div>
      </header>

      {/* ── HERO SECTION & INTERACTIVE TABS ─────────────────────────────────── */}
      <main className="relative z-10 pt-28 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Breadcrumb & Home Link */}
        <div className="flex items-center gap-2 text-xs font-mono text-zinc-400 mb-6">
          <Link to="/" className="inline-flex items-center gap-1 text-zinc-400 hover:text-[#00FF88] transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Home
          </Link>
          <span>/</span>
          <span className="text-zinc-500 uppercase">Legal Center</span>
          <span>/</span>
          <span className="text-[#00FF88] font-bold">{activePolicy.title}</span>
        </div>

        {/* Hero Banner Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative overflow-hidden rounded-3xl border border-white/[0.08] bg-gradient-to-br from-[#090C12]/90 via-[#06080E]/90 to-[#040508]/90 p-6 sm:p-10 shadow-2xl backdrop-blur-xl mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="space-y-3 max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-jetbrains font-bold uppercase tracking-wider border"
                style={{ color: activePolicy.color, borderColor: `${activePolicy.color}40`, backgroundColor: `${activePolicy.color}10` }}>
                <Icon className="w-3.5 h-3.5" />
                <span>{activePolicy.eyebrow}</span>
              </div>
              <h1 className="text-3xl sm:text-5xl font-black font-outfit text-white tracking-tight leading-tight">
                {activePolicy.title}
              </h1>
              <p className="text-sm sm:text-base text-zinc-300 leading-relaxed font-plex">
                {activePolicy.summary}
              </p>
              <div className="flex items-center gap-4 text-xs font-jetbrains text-zinc-400 pt-2">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-[#00FF88] animate-pulse" />
                  {activePolicy.lastUpdated}
                </span>
                <span>•</span>
                <span className="text-zinc-400">Jaipur, Rajasthan, India</span>
              </div>
            </div>

            {/* Quick Action Tools */}
            <div className="flex flex-wrap lg:flex-col gap-2.5 shrink-0">
              <button
                onClick={handlePrint}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-xs font-semibold text-zinc-200 hover:text-white hover:bg-white/[0.08] transition-all"
              >
                <Printer className="w-4 h-4 text-[#00F0FF]" />
                <span>Print Document</span>
              </button>
              <button
                onClick={handleCopyEmail}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-xs font-semibold text-zinc-200 hover:text-white hover:bg-white/[0.08] transition-all"
              >
                <Mail className="w-4 h-4 text-[#00FF88]" />
                <span>Email Legal Team</span>
              </button>
              <a
                href="https://jamadhan.com/contact"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-xs font-semibold text-zinc-200 hover:text-white hover:bg-white/[0.08] transition-all"
              >
                <ExternalLink className="w-4 h-4 text-[#FFCC00]" />
                <span>Official Contact</span>
              </a>
            </div>
          </div>

          {/* 3 Highlight Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5 mt-8 pt-8 border-t border-white/[0.06]">
            {activePolicy.highlights.map((h) => (
              <div key={h.label} className="p-4 rounded-2xl bg-white/[0.02] border border-white/[0.05] flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#00FF88] shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-xs font-bold text-white font-outfit uppercase tracking-wider">{h.label}</h4>
                  <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{h.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* ── POLICY SWITCHER TABS ─────────────────────────────────────────── */}
        <div className="flex items-center gap-2 overflow-x-auto pb-4 scrollbar-hide mb-8">
          {[
            { key: "privacy", label: "Privacy Policy", icon: ShieldCheck, path: "/privacy-policy/" },
            { key: "terms", label: "Terms & Conditions", icon: FileText, path: "/terms-and-conditions/" },
            { key: "refunds", label: "Refund & Cancellation", icon: RotateCcw, path: "/refund-and-cancellation-policy/" },
          ].map((tab) => {
            const TabIcon = tab.icon;
            const isActive = activeKey === tab.key;
            return (
              <Link
                key={tab.key}
                to={tab.path}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs sm:text-sm font-bold font-outfit transition-all duration-200 shrink-0 border ${
                  isActive
                    ? "bg-[#00FF88] text-black border-[#00FF88] shadow-[0_0_20px_rgba(0,255,136,0.35)]"
                    : "bg-white/[0.03] border-white/[0.08] text-zinc-400 hover:text-white hover:border-white/20"
                }`}
              >
                <TabIcon className="w-4 h-4" />
                <span>{tab.label}</span>
              </Link>
            );
          })}
        </div>

        {/* ── MAIN CONTENT GRID: STICKY TOC (LEFT) + DOCUMENT BODY (RIGHT) ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* ── LEFT COLUMN: STICKY TOC & SEARCH (4 COLS) ──────────────────── */}
          <aside className="lg:col-span-4 lg:sticky lg:top-24 space-y-4">
            {/* Live Search Input */}
            <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/[0.08] backdrop-blur">
              <div className="relative">
                <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={`Search ${activePolicy.title}...`}
                  className="w-full bg-[#0C0E14] border border-white/10 rounded-xl pl-9 pr-3 py-2 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#00FF88] font-jetbrains transition-colors"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-zinc-500 hover:text-white"
                  >
                    ✕
                  </button>
                )}
              </div>
              {searchQuery && (
                <p className="text-[10px] font-mono text-zinc-400 mt-2">
                  Showing {filteredSections.length} matching section{filteredSections.length !== 1 ? "s" : ""}
                </p>
              )}
            </div>

            {/* Table of Contents Box */}
            <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/[0.08] backdrop-blur">
              <h3 className="text-xs font-mono font-bold uppercase tracking-wider text-[#00F0FF] mb-3 flex items-center gap-1.5">
                <BookOpen className="w-3.5 h-3.5" />
                Table of Contents
              </h3>
              <nav className="space-y-1 text-xs">
                {activePolicy.sections.map((sec, idx) => {
                  const isActive = activeSectionId === sec.id;
                  return (
                    <button
                      key={sec.id}
                      onClick={() => scrollToSection(sec.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg font-medium transition-all flex items-center justify-between group ${
                        isActive
                          ? "bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20 font-bold"
                          : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
                      }`}
                    >
                      <span className="truncate pr-2">
                        {idx + 1}. {sec.title}
                      </span>
                      <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${isActive ? "text-[#00FF88] translate-x-0.5" : "text-zinc-600 group-hover:text-zinc-400"}`} />
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Quick Contact Card */}
            <div className="p-5 rounded-2xl bg-gradient-to-br from-white/[0.03] to-transparent border border-white/[0.08] space-y-3">
              <div className="flex items-center gap-2 text-white font-outfit font-bold text-sm">
                <Building2 className="w-4 h-4 text-[#00FF88]" />
                <span>Jama Dhan Institute</span>
              </div>
              <div className="pt-2 border-t border-white/[0.05] flex items-center justify-between text-xs">
                <span className="text-zinc-500 font-mono">Support:</span>
                <a href="mailto:contactus@jamadhan.com" className="text-[#00FF88] hover:underline font-mono">
                  contactus@jamadhan.com
                </a>
              </div>
            </div>
          </aside>

          {/* ── RIGHT COLUMN: FULL LEGAL CLAUSES & ARTICLES (8 COLS) ────────── */}
          <div className="lg:col-span-8 space-y-6">
            {filteredSections.length === 0 ? (
              <div className="p-8 rounded-2xl border border-white/[0.08] bg-white/[0.02] text-center space-y-3">
                <Info className="w-8 h-8 text-amber-400 mx-auto" />
                <h3 className="text-base font-bold text-white">No matching clauses found</h3>
                <p className="text-xs text-zinc-400">Try searching for keywords like "cookies", "refund", "data", or "Jaipur".</p>
                <button
                  onClick={() => setSearchQuery("")}
                  className="px-4 py-1.5 rounded-lg bg-[#00FF88] text-black font-bold text-xs font-outfit"
                >
                  Clear Search
                </button>
              </div>
            ) : (
              filteredSections.map((section, sIdx) => (
                <article
                  key={section.id}
                  id={section.id}
                  data-legal-section
                  className="p-6 sm:p-8 rounded-2xl border border-white/[0.08] bg-[#0A0D14]/90 backdrop-blur-md shadow-xl hover:border-white/20 transition-all space-y-4"
                >
                  {/* Clause Header */}
                  <div className="flex items-start gap-3 pb-3 border-b border-white/[0.06]">
                    <div className="w-7 h-7 rounded-lg bg-[#00FF88]/10 border border-[#00FF88]/20 flex items-center justify-center font-mono font-black text-xs text-[#00FF88] shrink-0 mt-0.5">
                      {sIdx + 1}
                    </div>
                    <div>
                      <h2 className="text-lg sm:text-xl font-bold font-outfit text-white tracking-wide">
                        {section.title}
                      </h2>
                    </div>
                  </div>

                  {/* Direct Section Content */}
                  {section.content && (
                    <div className="space-y-3 text-xs sm:text-sm text-zinc-300 leading-relaxed font-plex">
                      {section.content.map((p, pIdx) => (
                        <p key={pIdx} className={p.startsWith("•") || p.startsWith("1.") || p.startsWith("2.") || p.startsWith("3.") ? "pl-3 text-zinc-300" : ""}>
                          {p}
                        </p>
                      ))}
                    </div>
                  )}

                  {/* Subsections if any */}
                  {section.subsections && (
                    <div className="space-y-5 pt-2">
                      {section.subsections.map((sub, subIdx) => (
                        <div key={subIdx} className="space-y-2.5 p-4 rounded-xl bg-white/[0.02] border border-white/[0.04]">
                          <h3 className="text-sm font-bold font-outfit text-[#00F0FF] flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF]" />
                            {sub.subtitle}
                          </h3>
                          <div className="space-y-2 text-xs sm:text-sm text-zinc-300 leading-relaxed font-plex">
                            {sub.content.map((p, pIdx) => (
                              <p key={pIdx} className={p.startsWith("•") || p.startsWith("1.") || p.startsWith("2.") || p.startsWith("3.") ? "pl-3 text-zinc-300" : ""}>
                                {p}
                              </p>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </article>
              ))
            )}

            {/* Bottom Support Banner */}
            <div className="p-6 rounded-2xl border border-[#00FF88]/20 bg-gradient-to-r from-[#00FF88]/5 via-transparent to-[#00F0FF]/5 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#00FF88]/10 border border-[#00FF88]/30 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-5 h-5 text-[#00FF88]" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-white font-outfit">Have questions about our legal policies?</h4>
                  <p className="text-xs text-zinc-400">Our compliance and support team is available 6 days a week.</p>
                </div>
              </div>
              <Link
                to="/contact"
                className="px-4 py-2 rounded-xl bg-[#00FF88] text-black font-bold text-xs font-outfit hover:brightness-110 transition-all shadow-md shrink-0"
              >
                Contact Support →
              </Link>
            </div>
          </div>

        </div>
      </main>

      {/* ── GLOBAL FOOTER ────────────────────────────────────────────────────── */}
      <Footer />
    </div>
  );
}
